/**
 * Step2ContactSelection Component
 *
 * Shows a table of all contacts from event-type-filtered organizations.
 * Features:
 * - REQUIRED: Month and event type filters must be selected first
 * - Checkbox per contact
 * - Pre-checked for contacts matching event type filter
 * - Manual add/remove
 * - Search/filter
 * - "Select All" / "Deselect All"
 * - Total selected count
 * - Visual grouping by organization
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Search,
  Users,
  Building2,
  CheckSquare,
  Square,
  Loader2,
  AlertCircle,
  Filter,
  Trophy,
  Music,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ArrowRight,
  ChevronDown,
  CalendarDays,
} from 'lucide-react';
import {
  useEpgContactFilter,
  type EPGContactWithOrg,
  type MonthCode,
  MONTH_CODES,
  contactMatchesEventType,
  getContactPrimaryEmail,
} from '../../hooks/useEpgContactFilter';
import { useEventOptions, type FilteredEventOption } from '../../hooks/useEventOptions';
import type { HMCEvent } from '../../hmcEventsStore';

interface Step2ContactSelectionProps {
  /** Selected events from Step 1 (used to derive event types and months) */
  selectedEvents: HMCEvent[];
  /** Currently selected contact IDs */
  selectedContactIds: Set<number>;
  /** Callback when selection changes */
  onSelectionChange: (ids: Set<number>, contacts: EPGContactWithOrg[]) => void;
  /** All contacts loaded (for passing to Step 3/4) */
  allContacts: EPGContactWithOrg[];
  onAllContactsLoaded: (contacts: EPGContactWithOrg[]) => void;
  /** Navigation */
  onBack: () => void;
  onNext: () => void;
}

// Month display names
const MONTH_NAMES: Record<MonthCode, string> = {
  JAN: 'January',
  FEB: 'February',
  MAR: 'March',
  APR: 'April',
  MAY: 'May',
  JUN: 'June',
  JUL: 'July',
  AUG: 'August',
  SEP: 'September',
  OCT: 'October',
  NOV: 'November',
  DEC: 'December',
};

// Event types available
const EVENT_TYPES = ['Sporting', 'Concerts'] as const;
type EventType = (typeof EVENT_TYPES)[number];

export const Step2ContactSelection: React.FC<Step2ContactSelectionProps> = ({
  selectedEvents,
  selectedContactIds,
  onSelectionChange,
  allContacts,
  onAllContactsLoaded,
  onBack,
  onNext,
}) => {
  const { contacts: loadedContacts, isLoading, error, loadContacts } = useEpgContactFilter();
  const {
    filteredOptions: availableEvents,
    isLoading: eventsLoading,
    loadEventOptions,
    filterByCategory,
    getEventOptionIds,
    getOptionById,
  } = useEventOptions();

  // Filter selection state (REQUIRED before loading contacts)
  const [selectedMonths, setSelectedMonths] = useState<Set<MonthCode>>(new Set());
  const [selectedEventTypes, setSelectedEventTypes] = useState<Set<EventType>>(new Set());
  const [selectedSpecificEvents, setSelectedSpecificEvents] = useState<Set<number>>(new Set());
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Mobile month dropdown state
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const monthDropdownRef = useRef<HTMLDivElement>(null);

  // Specific event dropdown state
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const eventDropdownRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [displayEventTypeFilter, setDisplayEventTypeFilter] = useState<'all' | 'sporting' | 'concerts'>('all');

  // Close month dropdown on outside click
  useEffect(() => {
    if (!monthDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target as Node)) {
        setMonthDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [monthDropdownOpen]);

  // Close event dropdown on outside click
  useEffect(() => {
    if (!eventDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target as Node)) {
        setEventDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [eventDropdownOpen]);

  // Load event options when months change
  useEffect(() => {
    if (selectedMonths.size > 0) {
      loadEventOptions(Array.from(selectedMonths));
      // Clear specific event selection when months change
      setSelectedSpecificEvents(new Set());
    }
  }, [selectedMonths, loadEventOptions]);

  // Filter event options by selected categories
  useEffect(() => {
    if (selectedEventTypes.size > 0) {
      filterByCategory(Array.from(selectedEventTypes));
    }
  }, [selectedEventTypes, filterByCategory]);

  // Check if filters are valid (at least one month and one event type)
  const canLoadContacts = selectedMonths.size > 0 && selectedEventTypes.size > 0;

  // Toggle month selection
  const toggleMonth = useCallback((month: MonthCode) => {
    setSelectedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) {
        next.delete(month);
      } else {
        next.add(month);
      }
      return next;
    });
  }, []);

  // Toggle event type selection
  const toggleEventType = useCallback((type: EventType) => {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
    // Clear specific event selection when event type changes
    setSelectedSpecificEvents(new Set());
  }, []);

  // Toggle specific event selection
  const toggleSpecificEvent = useCallback((eventId: number) => {
    setSelectedSpecificEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  // Load contacts with selected filters
  const handleLoadContacts = useCallback(async () => {
    if (!canLoadContacts) return;

    const eventTypes = Array.from(selectedEventTypes);
    const months = Array.from(selectedMonths);

    // Get all month-specific IDs for selected specific events
    let specificEventIds: number[] | undefined;
    if (selectedSpecificEvents.size > 0) {
      specificEventIds = [];
      for (const eventId of selectedSpecificEvents) {
        const monthIds = getEventOptionIds(eventId);
        specificEventIds.push(...monthIds);
      }
      // Deduplicate
      specificEventIds = [...new Set(specificEventIds)];
      console.log(`[Step2] Loading with ${selectedSpecificEvents.size} specific events, resolved to ${specificEventIds.length} month-specific IDs`);
    }

    await loadContacts(eventTypes, months, specificEventIds);
    setFiltersApplied(true);
  }, [canLoadContacts, selectedEventTypes, selectedMonths, selectedSpecificEvents, loadContacts, getEventOptionIds]);

  // Update parent when contacts are loaded
  React.useEffect(() => {
    if (filtersApplied && loadedContacts.length > 0 && allContacts.length === 0) {
      onAllContactsLoaded(loadedContacts);

      // Pre-select contacts that match the selected event types
      // Use the matchesSelectedEventTypes flag set by the hook
      const preSelectedIds = new Set<number>();
      for (const contact of loadedContacts) {
        if (contact.matchesSelectedEventTypes && getContactPrimaryEmail(contact)) {
          preSelectedIds.add(contact.person_id);
        }
      }

      // Only pre-select if no existing selection
      if (selectedContactIds.size === 0 && preSelectedIds.size > 0) {
        const selectedContacts = loadedContacts.filter((c) => preSelectedIds.has(c.person_id));
        onSelectionChange(preSelectedIds, selectedContacts);
      }
    }
  }, [filtersApplied, loadedContacts, allContacts.length, onAllContactsLoaded, selectedContactIds.size, onSelectionChange]);

  // Use allContacts if available, otherwise use loadedContacts
  const contactsToDisplay = allContacts.length > 0 ? allContacts : loadedContacts;

  // Filter contacts by search and event type
  const filteredContacts = useMemo(() => {
    let filtered = contactsToDisplay;

    // Filter by event type (display filter, not the required filter)
    if (displayEventTypeFilter !== 'all') {
      const targetType = displayEventTypeFilter === 'sporting' ? 'Sporting' : 'Concerts';
      filtered = filtered.filter((c) => contactMatchesEventType(c, targetType));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name?.toLowerCase().includes(query) ||
          String(c.first_name || '').toLowerCase().includes(query) ||
          String(c.last_name || '').toLowerCase().includes(query) ||
          c.organization_name?.toLowerCase().includes(query) ||
          getContactPrimaryEmail(c)?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [contactsToDisplay, searchQuery, displayEventTypeFilter]);

  // Group contacts by organization for visual grouping
  const groupedContacts = useMemo(() => {
    const groups = new Map<string, EPGContactWithOrg[]>();

    for (const contact of filteredContacts) {
      const orgName = contact.organization_name || 'Unknown Organization';
      if (!groups.has(orgName)) {
        groups.set(orgName, []);
      }
      groups.get(orgName)!.push(contact);
    }

    // Sort groups by org name
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredContacts]);

  // Toggle single contact selection
  const toggleContact = useCallback(
    (contact: EPGContactWithOrg) => {
      const newIds = new Set(selectedContactIds);

      if (newIds.has(contact.person_id)) {
        newIds.delete(contact.person_id);
      } else {
        newIds.add(contact.person_id);
      }

      const selectedContacts = contactsToDisplay.filter((c) => newIds.has(c.person_id));
      onSelectionChange(newIds, selectedContacts);
    },
    [selectedContactIds, contactsToDisplay, onSelectionChange]
  );

  // Select all filtered contacts
  const selectAll = useCallback(() => {
    const newIds = new Set(selectedContactIds);

    for (const contact of filteredContacts) {
      const email = getContactPrimaryEmail(contact);
      if (email) {
        newIds.add(contact.person_id);
      }
    }

    const selectedContacts = contactsToDisplay.filter((c) => newIds.has(c.person_id));
    onSelectionChange(newIds, selectedContacts);
  }, [filteredContacts, selectedContactIds, contactsToDisplay, onSelectionChange]);

  // Deselect all filtered contacts
  const deselectAll = useCallback(() => {
    const filteredIds = new Set(filteredContacts.map((c) => c.person_id));
    const newIds = new Set(selectedContactIds);

    for (const id of filteredIds) {
      newIds.delete(id);
    }

    const selectedContacts = contactsToDisplay.filter((c) => newIds.has(c.person_id));
    onSelectionChange(newIds, selectedContacts);
  }, [filteredContacts, selectedContactIds, contactsToDisplay, onSelectionChange]);

  // Check if all filtered contacts with email are selected
  const allFilteredSelected = useMemo(() => {
    const selectableContacts = filteredContacts.filter((c) => getContactPrimaryEmail(c));
    if (selectableContacts.length === 0) return false;
    return selectableContacts.every((c) => selectedContactIds.has(c.person_id));
  }, [filteredContacts, selectedContactIds]);

  // Stats
  const totalContacts = contactsToDisplay.length;
  const totalSelected = selectedContactIds.size;

  // Reset filters to change selection
  const handleChangeFilters = useCallback(() => {
    setFiltersApplied(false);
    setSelectedSpecificEvents(new Set());
    onAllContactsLoaded([]);
    onSelectionChange(new Set(), []);
  }, [onAllContactsLoaded, onSelectionChange]);

  // Render filter selection UI (before contacts are loaded)
  if (!filtersApplied) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-dark-border/30">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-white font-medium">Select Filters</span>
          </div>
          <p className="mt-1 text-xs text-dark-muted hidden sm:block">
            Choose month(s) and event type(s) to load matching EPG contacts
          </p>
        </div>

        {/* Filter Selection */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-8">
            {/* Month Selection */}
            <div>
              <h3 className="flex items-center gap-2 text-sm font-medium text-white mb-2 sm:mb-4">
                <Calendar className="w-4 h-4 text-blue-400" />
                Select Month(s)
                <span className="text-red-400">*</span>
              </h3>

              {/* Mobile: Dropdown multi-select */}
              <div className="sm:hidden" ref={monthDropdownRef}>
                <button
                  onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-dark-bg border border-dark-border/30 rounded-xl text-sm"
                >
                  <span className={selectedMonths.size > 0 ? 'text-white' : 'text-dark-muted'}>
                    {selectedMonths.size > 0
                      ? `${selectedMonths.size} month${selectedMonths.size !== 1 ? 's' : ''} selected`
                      : 'Tap to select months'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-dark-muted transition-transform ${monthDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {monthDropdownOpen && (
                  <div className="absolute z-20 left-4 right-4 mt-1 bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 max-h-64 overflow-y-auto">
                    {MONTH_CODES.map((month) => (
                      <button
                        key={month}
                        onClick={() => toggleMonth(month)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-dark-border/50 transition-all duration-150"
                      >
                        <span className={selectedMonths.has(month) ? 'text-blue-400' : 'text-dark-text'}>
                          {MONTH_NAMES[month]}
                        </span>
                        {selectedMonths.has(month) && (
                          <CheckSquare className="w-4 h-4 text-blue-400" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop: Grid of buttons */}
              <div className="hidden sm:grid grid-cols-2 md:grid-cols-4 gap-2">
                {MONTH_CODES.map((month) => (
                  <button
                    key={month}
                    onClick={() => toggleMonth(month)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                      selectedMonths.has(month)
                        ? 'bg-blue-500 text-white'
                        : 'bg-dark-bg border border-dark-border/30 text-dark-muted hover:text-white hover:border-dark-border/30'
                    }`}
                  >
                    {MONTH_NAMES[month]}
                  </button>
                ))}
              </div>

              {selectedMonths.size > 0 && (
                <p className="mt-2 text-xs text-blue-400">
                  {selectedMonths.size} month{selectedMonths.size !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Event Type Selection */}
            <div>
              <h3 className="flex items-center gap-2 text-sm font-medium text-white mb-2 sm:mb-4">
                <Users className="w-4 h-4 text-purple-400" />
                Select Event Type(s)
                <span className="text-red-400">*</span>
              </h3>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => toggleEventType('Sporting')}
                  className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    selectedEventTypes.has('Sporting')
                      ? 'border-orange-500 bg-orange-500/20'
                      : 'border-dark-border/30 bg-dark-bg hover:border-dark-border/30'
                  }`}
                >
                  <Trophy
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      selectedEventTypes.has('Sporting') ? 'text-orange-400' : 'text-dark-muted'
                    }`}
                  />
                  <span
                    className={`text-base sm:text-lg font-medium ${
                      selectedEventTypes.has('Sporting') ? 'text-orange-400' : 'text-dark-text'
                    }`}
                  >
                    Sporting
                  </span>
                </button>

                <button
                  onClick={() => toggleEventType('Concerts')}
                  className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    selectedEventTypes.has('Concerts')
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-dark-border/30 bg-dark-bg hover:border-dark-border/30'
                  }`}
                >
                  <Music
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      selectedEventTypes.has('Concerts') ? 'text-purple-400' : 'text-dark-muted'
                    }`}
                  />
                  <span
                    className={`text-base sm:text-lg font-medium ${
                      selectedEventTypes.has('Concerts') ? 'text-purple-400' : 'text-dark-text'
                    }`}
                  >
                    Concerts
                  </span>
                </button>
              </div>
            </div>

            {/* Specific Event Selection (Optional) */}
            {selectedMonths.size > 0 && selectedEventTypes.size > 0 && (
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-white mb-2 sm:mb-4">
                  <CalendarDays className="w-4 h-4 text-green-400" />
                  Select Specific Event(s)
                  <span className="text-dark-muted text-xs font-normal">(Optional - narrows by org event)</span>
                </h3>

                {eventsLoading ? (
                  <div className="flex items-center gap-2 text-dark-muted text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading events...
                  </div>
                ) : availableEvents.length === 0 ? (
                  <p className="text-dark-muted text-sm">No events found for selected months and types</p>
                ) : (
                  <>
                    {/* Mobile: Dropdown multi-select */}
                    <div className="sm:hidden" ref={eventDropdownRef}>
                      <button
                        onClick={() => setEventDropdownOpen(!eventDropdownOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-dark-bg border border-dark-border/30 rounded-xl text-sm"
                      >
                        <span className={selectedSpecificEvents.size > 0 ? 'text-white' : 'text-dark-muted'}>
                          {selectedSpecificEvents.size > 0
                            ? `${selectedSpecificEvents.size} event${selectedSpecificEvents.size !== 1 ? 's' : ''} selected`
                            : 'All events (tap to filter)'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-dark-muted transition-transform ${eventDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {eventDropdownOpen && (
                        <div className="absolute z-20 left-4 right-4 mt-1 bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 max-h-64 overflow-y-auto">
                          {availableEvents.map((event) => (
                            <button
                              key={event.id}
                              onClick={() => toggleSpecificEvent(event.id)}
                              className="w-full flex items-center justify-between px-4 py-3 hover:bg-dark-border/50 transition-all duration-150"
                            >
                              <span className={selectedSpecificEvents.has(event.id) ? 'text-green-400' : 'text-dark-text'}>
                                {event.label}
                              </span>
                              {selectedSpecificEvents.has(event.id) && (
                                <CheckSquare className="w-4 h-4 text-green-400" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Desktop: Scrollable grid of checkboxes */}
                    <div className="hidden sm:block max-h-48 overflow-y-auto bg-dark-bg border border-dark-border/30 rounded-xl p-3">
                      <div className="grid grid-cols-2 gap-2">
                        {availableEvents.map((event) => (
                          <button
                            key={event.id}
                            onClick={() => toggleSpecificEvent(event.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all duration-150 ${
                              selectedSpecificEvents.has(event.id)
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                                : 'hover:bg-dark-border/50 text-dark-text'
                            }`}
                          >
                            {selectedSpecificEvents.has(event.id) ? (
                              <CheckSquare className="w-4 h-4 text-green-400 shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-dark-muted shrink-0" />
                            )}
                            <span className="truncate">{event.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {selectedSpecificEvents.size > 0 && (
                      <p className="mt-2 text-xs text-green-400">
                        {selectedSpecificEvents.size} specific event{selectedSpecificEvents.size !== 1 ? 's' : ''} selected
                        <button
                          onClick={() => setSelectedSpecificEvents(new Set())}
                          className="ml-2 text-dark-muted hover:text-dark-text underline"
                        >
                          Clear
                        </button>
                      </p>
                    )}

                    {selectedSpecificEvents.size === 0 && (
                      <p className="mt-2 text-xs text-dark-muted">
                        No specific events selected - will include all {selectedEventTypes.has('Sporting') ? 'Sporting' : ''}{selectedEventTypes.size === 2 ? ' & ' : ''}{selectedEventTypes.has('Concerts') ? 'Concerts' : ''} events
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Load Contacts Button */}
            <div className="pt-2 sm:pt-4">
              <button
                onClick={handleLoadContacts}
                disabled={!canLoadContacts || isLoading}
                className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl text-base sm:text-lg font-semibold hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading Contacts...
                  </>
                ) : (
                  <>
                    Load EPG Contacts
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {!canLoadContacts && (
                <p className="mt-3 text-center text-sm text-dark-muted">
                  Select at least one month and one event type to continue
                </p>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-dark-border/30">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-dark-muted hover:text-white transition-all duration-150 text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Template
          </button>
        </div>
      </div>
    );
  }

  // Render contact table (after filters applied)
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-dark-border/30 space-y-3 sm:space-y-4">
        {/* Filter badges */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-white font-medium">Select Recipients</span>
          </div>

          {/* Applied filter badges */}
          <div className="flex items-center gap-2 flex-wrap">
            {selectedEventTypes.has('Sporting') && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">
                <Trophy className="w-3 h-3" />
                Sporting
              </span>
            )}
            {selectedEventTypes.has('Concerts') && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                <Music className="w-3 h-3" />
                Concerts
              </span>
            )}
            <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
              <Calendar className="w-3 h-3" />
              {selectedMonths.size} month{selectedMonths.size !== 1 ? 's' : ''}
            </span>
            {selectedSpecificEvents.size > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                <CalendarDays className="w-3 h-3" />
                {selectedSpecificEvents.size} event{selectedSpecificEvents.size !== 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={handleChangeFilters}
              className="text-xs text-dark-muted hover:text-dark-text underline"
            >
              Change
            </button>
          </div>

          {/* Selection count badge */}
          <div className="ml-auto flex items-center gap-2 px-3 py-1 bg-blue-500/20 border border-brand-500/30 rounded-full">
            <span className="text-sm text-blue-400 font-medium">{totalSelected} selected</span>
            {totalContacts > 0 && (
              <span className="text-xs text-dark-muted">of {totalContacts}</span>
            )}
          </div>
        </div>

        {/* Search and filter controls */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or organization..."
              className="w-full pl-10 pr-4 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Event type filter (for display only) */}
          <div className="flex items-center gap-1 bg-dark-bg border border-dark-border/30 rounded-xl p-1">
            <button
              onClick={() => setDisplayEventTypeFilter('all')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 ${
                displayEventTypeFilter === 'all'
                  ? 'bg-purple-500 text-white'
                  : 'text-dark-muted hover:text-white'
              }`}
            >
              All
            </button>
            {selectedEventTypes.has('Sporting') && (
              <button
                onClick={() => setDisplayEventTypeFilter('sporting')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 flex items-center gap-1 ${
                  displayEventTypeFilter === 'sporting'
                    ? 'bg-orange-500 text-white'
                    : 'text-dark-muted hover:text-white'
                }`}
              >
                <Trophy className="w-3 h-3" />
                Sporting
              </button>
            )}
            {selectedEventTypes.has('Concerts') && (
              <button
                onClick={() => setDisplayEventTypeFilter('concerts')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 flex items-center gap-1 ${
                  displayEventTypeFilter === 'concerts'
                    ? 'bg-purple-500 text-white'
                    : 'text-dark-muted hover:text-white'
                }`}
              >
                <Music className="w-3 h-3" />
                Concerts
              </button>
            )}
          </div>

          {/* Select/Deselect All */}
          <div className="flex items-center gap-1">
            <button
              onClick={selectAll}
              className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded text-xs font-medium hover:bg-green-500/30 transition-all duration-150"
            >
              Select All
            </button>
            <button
              onClick={deselectAll}
              className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded text-xs font-medium hover:bg-red-500/30 transition-all duration-150"
            >
              Deselect All
            </button>
          </div>
        </div>
      </div>

      {/* Contact table - scrollbar always visible */}
      <div className="flex-1 overflow-y-scroll">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-dark-muted">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Loading contacts from EPG...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-red-400">
            <AlertCircle className="w-8 h-8" />
            <span className="text-sm">{error}</span>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-dark-muted">
            <Filter className="w-8 h-8" />
            <span className="text-sm">
              {contactsToDisplay.length === 0
                ? 'No contacts found for the selected filters'
                : 'No contacts match the current search'}
            </span>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg border-b border-dark-border/30">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <button
                    onClick={allFilteredSelected ? deselectAll : selectAll}
                    className="p-1 text-dark-muted hover:text-white transition-all duration-150"
                    title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                  >
                    {allFilteredSelected ? (
                      <CheckSquare className="w-4 h-4 text-purple-400" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider hidden lg:table-cell">
                  Work Address
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                  Event Types
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedContacts.map(([orgName, orgContacts]) => (
                <React.Fragment key={orgName}>
                  {/* Organization header row */}
                  <tr className="bg-dark-bg/30">
                    <td colSpan={7} className="px-4 py-2">
                      <div className="flex items-center gap-2 text-xs text-dark-muted">
                        <Building2 className="w-3 h-3" />
                        <span className="font-medium">{orgName}</span>
                        <span className="text-dark-muted">({orgContacts.length} contacts)</span>
                      </div>
                    </td>
                  </tr>

                  {/* Contact rows */}
                  {orgContacts.map((contact) => {
                    const email = getContactPrimaryEmail(contact);
                    const isSelected = selectedContactIds.has(contact.person_id);
                    const hasEmail = !!email;

                    // Determine which event types the contact matches
                    const matchesSporting = contactMatchesEventType(contact, 'Sporting');
                    const matchesConcerts = contactMatchesEventType(contact, 'Concerts');

                    return (
                      <tr
                        key={contact.person_id}
                        onClick={() => hasEmail && toggleContact(contact)}
                        className={`border-b border-dark-border/30 transition-all duration-150 ${
                          hasEmail
                            ? 'cursor-pointer hover:bg-dark-surface/50'
                            : 'opacity-50 cursor-not-allowed'
                        } ${isSelected ? 'bg-purple-500/10' : ''}`}
                      >
                        <td className="px-4 py-3">
                          {hasEmail ? (
                            isSelected ? (
                              <CheckSquare className="w-4 h-4 text-purple-400" />
                            ) : (
                              <Square className="w-4 h-4 text-dark-muted" />
                            )
                          ) : (
                            <Square className="w-4 h-4 text-dark-muted" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-white">
                            {contact.name}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-dark-muted">
                            {contact.title || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-sm text-dark-muted truncate max-w-[150px] block">
                            {contact.work_location || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-dark-muted">{contact.organization_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          {email ? (
                            <span className="text-sm text-blue-400">{email}</span>
                          ) : (
                            <span className="text-sm text-dark-muted italic">No email</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {matchesSporting && (
                              <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">
                                Sporting
                              </span>
                            )}
                            {matchesConcerts && (
                              <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                                Concerts
                              </span>
                            )}
                            {!matchesSporting && !matchesConcerts && (
                              <span className="text-xs text-dark-muted">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer with navigation */}
      <div className="p-3 sm:p-4 border-t border-dark-border/30 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-dark-muted hover:text-white transition-all duration-150 text-sm order-2 sm:order-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Template
        </button>

        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 order-1 sm:order-2">
          <span className="text-xs sm:text-sm text-dark-muted">
            {totalSelected} selected
          </span>
          <button
            onClick={onNext}
            disabled={totalSelected === 0}
            className="flex items-center gap-2 px-4 sm:px-5 py-2 bg-purple-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-purple-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2ContactSelection;
